import dynamic from 'next/dynamic';
import React from 'react';
import URI from 'urijs';
import { GOOGLE_API_KEY, GOOGLE_API_STATIC_PATH } from 'Components/utils/google/google.constants';
import { getStaticMapURL } from 'Components/utils/google/google.utils';
import config from 'Config';
import { useAppContext } from 'Context/ReactAppContext';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { Map } from './Map';
const GoogleMapReact = dynamic(() => import('google-map-react'), {
  ssr: false,
});
export type Props = {
  className?: string;
  closeLabel?: string;
  format?: string;
  height?: number;
  latitude?: number;
  longitude?: number;
  maptype?: string;
  scale?: number;
  title?: string;
  width?: number;
  zoom?: number;
};

const createMapOptions = () => {
  return {
    fullscreenControl: false,
  };
};

const addMarker = ({ map, maps, position, icon }) => {
  // eslint-disable-next-line no-new
  new maps.Marker({
    icon,
    map,
    position,
  });
};

export const GoogleMap = ({
  className,
  closeLabel,
  format = 'jpeg',
  height = 420,
  latitude = 0,
  longitude = 0,
  maptype = 'roadmap',
  scale = 2,
  title,
  width = 780,
  zoom = 8,
}: Props) => {
  const track = useTracking();
  const coordinates = {
    lat: latitude,
    lng: longitude,
  };
  const appContext = useAppContext();
  const origin = new URI(appContext.originalUrl).origin();
  const iconUrl = `${origin}/markers/marker.png`;
  const staticMapOptions = {
    center: `${latitude},${longitude}`,
    format,
    language: appContext.locale,
    maptype,
    markers: `icon:${iconUrl}|scale:${scale}|${latitude},${longitude}`,
    scale,
    size: `${width}x${height}`,
    zoom,
  };
  return (
    <Map
      className={className}
      closeLabel={closeLabel}
      dataTestId="map-preview"
      imageSrc={getStaticMapURL(GOOGLE_API_STATIC_PATH, staticMapOptions)}
      onClick={() =>
        track({
          categoryGA: 'resort',
          actionGA: 'mapClick',
          labelGA: 'GoogleMap',
        })
      }
      ratio={{
        w: width,
        h: height,
      }}
      title={title}
    >
      <div className="h-full">
        <GoogleMapReact
          bootstrapURLKeys={{
            key: config.get(GOOGLE_API_KEY),
          }}
          defaultCenter={coordinates}
          defaultZoom={zoom}
          onGoogleApiLoaded={({ map, maps }) =>
            addMarker({
              map,
              maps,
              position: coordinates,
              icon: iconUrl,
            })
          }
          options={createMapOptions}
          yesIWantToUseGoogleMapApiInternals
        />
      </div>
    </Map>
  );
};
